import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Globe2, Loader2, MapPin, Plane, Search, CalendarDays, BookOpen } from "lucide-react";
import api from "../lib/api";
import { placePhotoQuery, HoverPhotoPreview } from "../components/UnsplashGallery";

/* Top 500 Global Tourist Destinations — master database directory. */
const sel = "border border-zinc-300 bg-white px-3 py-2 text-sm font-600";

export default function TopDestinations() {
  const [data, setData] = useState(null);
  const [continent, setContinent] = useState("");
  const [type, setType] = useState("");
  const [q, setQ] = useState("");
  const [guideOnly, setGuideOnly] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => { document.title = "Top 500 Global Tourist Destinations | TravelerPulse.pro"; }, []);

  useEffect(() => {
    setLoading(true);
    const t = setTimeout(() => {
      api.get("/top-destinations", { params: { continent, type, q, guide: guideOnly ? 1 : "", limit: 90 } })
        .then((r) => setData(r.data)).catch(() => setData({ total: 0, items: [], facets: {} }))
        .finally(() => setLoading(false));
    }, q ? 350 : 0);
    return () => clearTimeout(t);
  }, [continent, type, q, guideOnly]);

  return (
    <div className="min-h-screen bg-zinc-50" data-testid="top-destinations-page">
      <div className="bg-zinc-900 text-white">
        <div className="max-w-6xl mx-auto px-5 lg:px-8 py-12">
          <h1 className="font-heading font-900 text-4xl sm:text-5xl flex items-center gap-3">
            <Globe2 className="w-10 h-10 text-[#FBBC05]" /> Top 500 Destinations
          </h1>
          <p className="text-zinc-300 mt-3 text-base md:text-lg max-w-2xl">
            The master database of the world's most important places — airports, coordinates,
            best time to visit, typical stay and live guide links for every entry.
          </p>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-5 lg:px-8 py-8">
        {/* Tabs: everything vs. places with published guides/articles */}
        <div className="flex gap-1 mb-4 border-b border-zinc-300">
          <button onClick={() => setGuideOnly(false)} data-testid="top-dest-tab-all"
            className={`px-4 py-2 text-sm font-700 border-b-2 -mb-px ${!guideOnly ? "border-[#1a73e8] text-zinc-900" : "border-transparent text-muted-foreground hover:text-zinc-900"}`}>
            All places
          </button>
          <button onClick={() => setGuideOnly(true)} data-testid="top-dest-tab-guides"
            className={`px-4 py-2 text-sm font-700 border-b-2 -mb-px inline-flex items-center gap-1.5 ${guideOnly ? "border-[#1a73e8] text-zinc-900" : "border-transparent text-muted-foreground hover:text-zinc-900"}`}>
            <BookOpen className="w-4 h-4" /> With travel guides
            {data?.facets?.with_guides != null && <span className="text-[10px] font-800 bg-emerald-100 text-emerald-800 px-1.5 py-0.5 rounded-full">{data.facets.with_guides}</span>}
          </button>
        </div>
        <div className="flex flex-wrap gap-2 mb-5 items-center">
          <div className="relative flex">
            <Search className="w-4 h-4 absolute left-3 top-2.5 text-zinc-400" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search places, tags…"
              className={`${sel} pl-9 w-56`} data-testid="top-dest-search" />
            <button onClick={() => setQ((v) => v.trim())} data-testid="top-dest-search-go"
              className="px-3 bg-[#1a73e8] text-white text-xs font-800 hover:opacity-90">Go&nbsp;&rsaquo;</button>
          </div>
          <select value={continent} onChange={(e) => setContinent(e.target.value)} className={sel} data-testid="top-dest-continent">
            <option value="">All continents</option>
            {(data?.facets?.continents || []).map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
          <select value={type} onChange={(e) => setType(e.target.value)} className={sel} data-testid="top-dest-type">
            <option value="">All types</option>
            {(data?.facets?.types || []).map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
          {data && <span className="text-xs text-muted-foreground font-mono" data-testid="top-dest-count">{data.total} places</span>}
          {loading && <Loader2 className="w-4 h-4 animate-spin text-zinc-400" />}
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {(data?.items || []).map((d) => (
            <div key={d.slug} className="bg-white border border-zinc-300 p-4" data-testid={`top-dest-${d.slug.split("/").pop()}`}>
              <div className="flex items-start justify-between gap-2">
                <HoverPhotoPreview query={placePhotoQuery(d.name || d.label, d.country)} name={d.label}
                  sub={d.country} icon={true} testId={`top-dest-preview-${d.slug.split("/").pop()}`} className="min-w-0">
                  <div className="min-w-0 cursor-default inline-block align-middle">
                    <div className="font-heading font-900 text-base truncate">{d.label}</div>
                    <div className="text-xs text-muted-foreground truncate">
                      {[d.state !== d.name ? d.state : null, d.country, d.continent].filter(Boolean).join(" · ")}
                    </div>
                  </div>
                </HoverPhotoPreview>
                <span className="shrink-0 text-[9px] font-800 uppercase px-1.5 py-0.5 bg-zinc-100 text-zinc-600">{d.type}</span>
              </div>
              <div className="mt-2 space-y-1 text-xs text-zinc-700">
                {(d.nearby_airports || []).length > 0 && (
                  <div className="flex items-start gap-1.5">
                    <Plane className="w-3.5 h-3.5 mt-0.5 text-[#4285F4] shrink-0" />
                    <span>{d.nearby_airports.map((a) => `${a.iata} (${a.distance_km} km)`).join(" · ")}</span>
                  </div>
                )}
                {d.best_time && (
                  <div className="flex items-start gap-1.5">
                    <CalendarDays className="w-3.5 h-3.5 mt-0.5 text-[#34A853] shrink-0" />
                    <span className="line-clamp-1">{d.best_time}{d.typical_stay ? ` · stay ${d.typical_stay}` : ""}</span>
                  </div>
                )}
                {d.lat != null && (
                  <div className="flex items-center gap-1.5">
                    <MapPin className="w-3.5 h-3.5 text-[#EA4335] shrink-0" />
                    <span className="font-mono">{d.lat.toFixed(2)}, {d.lon.toFixed(2)}</span>
                  </div>
                )}
              </div>
              {(d.tags || []).length > 0 && (
                <div className="mt-2 flex flex-wrap gap-1">
                  {d.tags.slice(0, 3).map((t) => <span key={t} className="text-[10px] px-1.5 py-0.5 bg-blue-50 text-blue-800">{t}</span>)}
                </div>
              )}
              <div className="mt-3 flex gap-2">
                <Link to={d.guide_link} className="inline-flex items-center gap-1 px-2.5 py-1.5 bg-[#1a73e8] text-white text-xs font-800 hover:opacity-90">
                  <BookOpen className="w-3.5 h-3.5" /> {d.guide_status === "PUBLISHED" ? "Guide" : "Create guide"}
                </Link>
                <Link to={d.visa_link} className="px-2.5 py-1.5 border border-zinc-300 text-xs font-700 hover:border-zinc-600">Visas</Link>
              </div>
            </div>
          ))}
        </div>
        {data && data.items.length === 0 && !loading && (
          <p className="text-sm text-muted-foreground mt-6">No places match those filters.</p>
        )}
        <p className="mt-6 text-[10px] text-zinc-400">
          Hover a place name to preview photos — courtesy of the photographers on{" "}
          <a href="https://unsplash.com/?utm_source=travelerpulse&utm_medium=referral" target="_blank"
            rel="noreferrer" className="underline">Unsplash</a>.
        </p>
      </div>
    </div>
  );
}
